<template>
    <a-modal
        :visible="true"
        :width="1200"
        :footer="null"
        :closable="false"
        :maskClosable="false"
        :bodyStyle="{ padding: '24px', maxHeight: '80vh', overflowY: 'auto' }"
        wrapClassName="table-ai-agent-modal"
        @cancel="handleClose"
    >
        <div class="table-ai-agent" @keydown="handleKeyDown" @keyup="handleKeyUp" tabindex="0" ref="wizardOverlay">
            <div class="wizard-header">
                <h3 class="d-flex align-items-center gap-5">
                    <img src="/static/img/copilot.png" width="30" height="30" />
                    {{ $t("tables.supsisAssistantTables") }}
                </h3>
                <a-icon type="close" class="close-button" @click="handleClose" />
            </div>

            <!-- Aşama 1: Prompt Girişi -->
            <div v-if="!commands.length" class="prompt-stage">
                <div class="prompt-container">
                    <h4>{{ $t('tables.supsisAssistantWritePrompt') }}</h4>
                    <div class="assistant-capabilities">
                        <div class="capabilities-header">
                            <a-icon type="bulb" />
                            <h5>{{ $t('tables.assistantCapabilities') }}</h5>
                        </div>
                        <div class="capabilities-content">
                            <div class="capability-item">
                                <i class="material-icons">build</i>
                                <span>{{ $t('tables.addNewFieldsToTable') }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">edit</i>
                                <span>{{ $t('tables.editExistingFields') }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">delete</i>
                                <span>{{ $t('tables.deleteUnnecessaryFields') }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">filter_list</i>
                                <span>{{ $t('tables.filterRecords') }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">group_work</i>
                                <span>{{ $t('tables.groupRecords') }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">visibility_off</i>
                                <span>{{ $t('tables.hideFieldsCapability') }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">format_color_fill</i>
                                <span>{{ $t('tables.createConditionalFormatting') }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">functions</i>
                                <span>{{ $t('tables.createCalculationFormulas') }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">add_box</i>
                                <span>{{ $t('tables.createSampleRecords') }}</span>
                            </div>
                        </div>
                    </div>
                    <s2-textarea
                        v-model="customerPrompt"
                        :rows="4"
                        :placeholder="$t('tables.examplePrompt')"
                        ref="promptTextarea"
                    />
                    <a-button
                        type="primary"
                        :loading="copilotWaiting"
                        @click="handleAIRecommendation"
                        class="submit-button"
                    >
                        {{ $t("tables.sendSupsisAssistant") }}
                    </a-button>
                </div>

                <!-- Örnek Promptlar Slider -->
                <div class="example-prompts">
                    <h4>{{ $t('tables.examplePromptsTitle') }}</h4>
                    <div class="prompt-slider-container">
                        <div class="prompt-slider" ref="slider">
                            <div class="prompt-slide" v-for="(prompt, index) in examplePrompts" :key="index">
                                <div class="prompt-card" @click="selectExamplePrompt(prompt)">
                                    <a-icon type="bulb" />
                                    <p>{{ prompt }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="slider-controls">
                            <a-button class="control-btn prev" @click="slidePrev">
                                <a-icon type="left" />
                            </a-button>
                            <a-button class="control-btn next" @click="slideNext">
                                <a-icon type="right" />
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Aşama 2: İşlem Adımları -->
            <div v-else>
                <a-progress :percent="progressPercent" stroke-color="#1890ff" show-info style="margin-bottom: 16px" />
                <!-- Adım Listesi -->
                <div class="wizard-steps">
                    <div v-for="(step, index) in steps" :key="index" class="wizard-step" :class="step.status">
                        <a-icon
                            v-if="step.status === 'waiting'"
                            type="pause-circle"
                            theme="filled"
                            style="color: #ccc; margin-right: 8px"
                        />
                        <a-icon
                            v-else-if="step.status === 'in-progress'"
                            type="loading"
                            style="color: #1890ff; margin-right: 8px"
                        />
                        <a-icon
                            v-else-if="step.status === 'success'"
                            type="check-circle"
                            theme="filled"
                            style="color: #52c41a; margin-right: 8px"
                        />
                        <a-icon
                            v-else-if="step.status === 'error'"
                            type="close-circle"
                            theme="filled"
                            style="color: #f5222d; margin-right: 8px"
                        />
                        <span>{{ step.displayLabel }}</span>
                    </div>
                </div>
            </div>
        </div>
    </a-modal>
</template>

<script>
import _ from "lodash";
import { TABLE_API } from "../../../api/table";
import { mapActions, mapState } from "vuex";
import CoPilot from "../../../utils/CoPilot";
import confetti from "canvas-confetti";
import S2Textarea from "../../../components/S2Textarea.vue";

export default {
    name: "TableAiAgent",
    components: {
        S2Textarea,
    },
    props: {
        tableId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            steps: [], // { displayLabel, status: 'waiting'|'in-progress'|'success'|'error', commandIndex: number }
            currentCommandIndex: 0,
            totalCommands: 0,
            fields: [],
            commands: [],
            copilotWaiting: false,
            customerPrompt: "",
            examplePrompts: [
                this.$t('tables.examplePrompts.addCustomerInfoFields'),
                this.$t('tables.examplePrompts.addOrderFields'),
                this.$t('tables.examplePrompts.addBirthDateField'),
                this.$t('tables.examplePrompts.updateStatusField'),
                this.$t('tables.examplePrompts.replaceAddressFields'),
                this.$t('tables.examplePrompts.alignTextFields'),
                this.$t('tables.examplePrompts.addAutoIncrementID'),
                this.$t('tables.examplePrompts.addTotalAmountFormula'),
                this.$t('tables.examplePrompts.createActiveCustomersFilter'),
                this.$t('tables.examplePrompts.filterRecentUsers'),
                this.$t('tables.examplePrompts.groupByStatus'),
                this.$t('tables.examplePrompts.hideAllExceptDeliveryAndOrderNo'),
                this.$t('tables.examplePrompts.colorCodeOrders'),
                this.$t('tables.examplePrompts.highlightHighPaymentRecords'),
                this.$t('tables.examplePrompts.groupAndHighlightUnpaidOrders'),
                this.$t('tables.examplePrompts.createSampleRecords'),
                this.$t('tables.examplePrompts.addSampleRecordsForAliAndAhmet'),
                this.$t('tables.examplePrompts.createRandomSampleOrders'),
            ],
            currentSlide: 0,
            slideInterval: null,
        };
    },
    computed: {
        ...mapState({
            tables: (state) => state.table.tables,
        }),
        progressPercent() {
            if (this.totalCommands === 0) {
                return 0;
            }
            const completedCount = this.steps.filter(function (s) {
                return s.status === "success";
            }).length;
            return Math.round((completedCount / this.totalCommands) * 100);
        },
        currentTable() {
            return this.tables.find((table) => table._id === this.tableId);
        },
    },
    mounted() {
        this.startSlideShow();
        // Form overlay'ine focus ver - böylece keydown eventlerini alabilir
        this.$nextTick(() => {
            if (this.$refs.wizardOverlay) {
                this.$refs.wizardOverlay.focus();
            }
        });
    },
    beforeDestroy() {
        this.stopSlideShow();
    },
    methods: {
        ...mapActions({
            syncTables: "table/syncTables",
        }),
        async handleAIRecommendation() {
            try {
                this.copilotWaiting = true;
                const copilot = CoPilot.getInstance();
                const recommendation = await copilot.runTableAgent({
                    siteId: this.$store.state.site.site._id,
                    tableId: this.tableId,
                    customerPrompt: this.customerPrompt,
                });
                console.log("[handleAIRecommendation] recommendation received", recommendation);
                if (!recommendation) {
                    this.$message.error("AI önerileri alınamadı");
                    this.copilotWaiting = false;
                    return;
                }
                this.commands = recommendation;
                this.initSteps();
                this.startProcessing();
            } catch (error) {
                console.error(error);
                this.$message.error("AI önerileri alınamadı");
            } finally {
                this.copilotWaiting = false;
            }
        },
        initSteps() {
            const localCommands = this.commands || [];
            this.totalCommands = localCommands.length;

            // Her bir komutu UI'da gösterecek bir "step" nesnesi oluşturuyoruz
            this.steps = localCommands.map((cmd, index) => {
                const displayLabel = this.createDisplayLabel(cmd);
                return {
                    displayLabel: displayLabel,
                    status: "waiting",
                    commandIndex: index,
                };
            });
        },

        createDisplayLabel(cmd) {
            // operation'a göre kullanıcıya anlamlı bir açıklama oluştur
            let fieldName = cmd.payload && cmd.payload.name ? cmd.payload.name : "";

            if (cmd.operation === "create-table-field") {
                const fieldType = cmd.payload.fieldType || "alan";
                return `"${fieldName}" ${fieldType} alanı oluşturulması`;
            } else if (cmd.operation === "update-table-field") {
                const fieldId = cmd.payload.fieldId;
                // Mevcut field ismini bulmaya çalışalım
                const existingField =
                    this.fields.find((f) => f._id === fieldId) ||
                    (this.currentTable && this.currentTable.fields
                        ? this.currentTable.fields.find((f) => f._id === fieldId)
                        : null);

                const originalName = existingField ? existingField.name : "Alan";
                return `"${originalName}" alanının "${fieldName}" olarak güncellenmesi`;
            } else if (cmd.operation === "delete-table-field") {
                const fieldId = cmd.payload.fieldId;
                // Mevcut field ismini bulmaya çalışalım
                const existingField =
                    this.fields.find((f) => f._id === fieldId) ||
                    (this.currentTable && this.currentTable.fields
                        ? this.currentTable.fields.find((f) => f._id === fieldId)
                        : null);

                const fieldName = existingField ? existingField.name : "Bilinmeyen Alan";
                return `"${fieldName}" alanının silinmesi`;
            } else if (cmd.operation === "create-table-filter") {
                return "Tablo filtreleme oluşturulması";
            } else if (cmd.operation === "create-table-group") {
                // Grup alanını bul
                const fieldId = cmd.payload.fieldId;
                const field =
                    this.currentTable && this.currentTable.fields
                        ? this.currentTable.fields.find((f) => f._id === fieldId)
                        : null;
                const fieldName = field ? field.name : "Bilinmeyen Alan";
                return `"${fieldName}" alanına göre gruplandırma`;
            } else if (cmd.operation === "create-table-hidden-fields") {
                const count = cmd.payload.hiddenFields ? cmd.payload.hiddenFields.length : 0;
                return `${count} alanın gizlenmesi`;
            } else if (cmd.operation === "create-table-color-conditions") {
                const count = cmd.payload.colorConditions ? cmd.payload.colorConditions.length : 0;
                return `${count} renklendirme kuralı oluşturulması`;
            } else if (cmd.operation === "create-mock-records") {
                let recordCount = 0;

                if (Array.isArray(cmd.payload)) {
                    recordCount = cmd.payload.length;
                } else if (cmd.payload && cmd.payload.count) {
                    recordCount = cmd.payload.count;
                }

                return `${recordCount} adet örnek kayıt oluşturulması`;
            }

            return "Bilinmeyen İşlem";
        },

        startProcessing() {
            this.processNextCommand();
        },

        sleep(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },

        async processNextCommand() {
            await this.sleep(Math.random() * 500 + 100);
            if (this.currentCommandIndex >= this.totalCommands) {
                this.$message.success("Tüm işlemler başarıyla tamamlandı");

                setTimeout(() => {
                    this.runSuccessConfetti();
                    this.$emit("close");
                }, 250); // 1 saniye bekleyip kapatıyoruz
                return; // İşlemler tamamlandı
            }

            const step = this.steps[this.currentCommandIndex];
            step.status = "in-progress";

            const cmd = this.commands[this.currentCommandIndex];
            this.executeCommand(cmd)
                .then((result) => {
                    step.status = "success";

                    // İşlem sonucuna göre event'leri gönderelim
                    if (cmd.operation === "create-table-field" && result) {
                        this.$emit("field-added", result);
                    } else if (cmd.operation === "update-table-field" && result) {
                        this.$emit("field-updated", result);
                    } else if (cmd.operation === "delete-table-field") {
                        this.$emit("field-deleted", cmd.payload.fieldId);
                    } else if (cmd.operation === "create-table-filter") {
                        this.$emit("filters-updated", result.filters);
                    } else if (cmd.operation === "create-table-group") {
                        this.$emit("group-updated", result.groupValue);
                    } else if (cmd.operation === "create-table-hidden-fields") {
                        this.$emit("hidden-fields-updated", result.hiddenFields);
                    } else if (cmd.operation === "create-table-color-conditions") {
                        this.$emit("color-conditions-updated", result.colorConditions);
                    }

                    // Eğer bu komutun sonucunda bir _id döndüyse, placeholder'ı ileride kullanacak şekilde replace edelim
                    if (result && result._id) {
                        const oldPlaceholder = cmd.payload && cmd.payload._id ? cmd.payload._id : null;
                        if (oldPlaceholder && oldPlaceholder.indexOf("{{") === 0) {
                            // Diğer komutlarda bu placeholder geçiyorsa ID ile değiştir
                            this.replaceAllPlaceholdersInCommands(oldPlaceholder, result._id);
                        }
                    }

                    this.currentCommandIndex++;
                    this.processNextCommand();
                })
                .catch((err) => {
                    // Hata durumunda step'i error'a çekelim ve duralım
                    step.status = "error";
                    console.error("Komut yürütülürken hata oluştu:", err);
                });
        },

        executeCommand(cmd) {
            if (!cmd || !cmd.operation) {
                return Promise.reject(new Error("Geçersiz komut"));
            }

            // Komut tipine göre ilgili API fonksiyonunu çağır
            if (cmd.operation === "create-table-field") {
                return this.handleCreateTableField(cmd.payload);
            } else if (cmd.operation === "update-table-field") {
                return this.handleUpdateTableField(cmd.payload);
            } else if (cmd.operation === "delete-table-field") {
                return this.handleDeleteTableField(cmd.payload);
            } else if (cmd.operation === "create-table-filter") {
                return this.handleCreateTableFilter(cmd.payload);
            } else if (cmd.operation === "create-table-group") {
                return this.handleCreateTableGroup(cmd.payload);
            } else if (cmd.operation === "create-table-hidden-fields") {
                return this.handleCreateTableHiddenFields(cmd.payload);
            } else if (cmd.operation === "create-table-color-conditions") {
                return this.handleCreateTableColorConditions(cmd.payload);
            } else if (cmd.operation === "create-mock-records") {
                return this.handleCreateMockRecords(cmd.payload);
            }

            return Promise.reject(new Error("Desteklenmeyen operasyon: " + cmd.operation));
        },

        handleCreateTableField(payload) {
            // Tabloya yeni alan ekleme
            const clonedData = _.cloneDeep(payload);
            // Eğer _id placehoder içeriyorsa, onu çıkart
            if (clonedData._id && clonedData._id.includes("{{")) {
                delete clonedData._id;
            }
            // Eğer payload içinde table yoksa, prop olarak geçilen tableId'yi kullan
            if (!clonedData.table) {
                clonedData.table = this.tableId;
            }

            return TABLE_API.createField(this.tableId, clonedData).then((createdField) => {
                this.syncTables();
                this.fields.push(createdField);
                return createdField;
            });
        },

        handleUpdateTableField(payload) {
            // Alan güncelleme
            const { table, fieldId } = payload;
            const tableId = table || this.tableId;

            // Payload'dan table ve fieldId çıkart, sadece güncellenecek alanları içersin
            const updatingFields = _.cloneDeep(payload);
            delete updatingFields.table;
            delete updatingFields.fieldId;

            return TABLE_API.updateField(tableId, fieldId, updatingFields).then((updatedField) => {
                this.syncTables();
                // Mevcut field'ı güncelle
                const index = this.fields.findIndex((f) => f._id === fieldId);
                if (index !== -1) {
                    this.fields[index] = updatedField;
                }
                return updatedField;
            });
        },

        handleDeleteTableField(payload) {
            // Alan silme
            const { table, fieldId } = payload;
            const tableId = table || this.tableId;

            return TABLE_API.deleteField(tableId, fieldId).then(() => {
                this.syncTables();
                // Fields listesinden sil
                this.fields = this.fields.filter((f) => f._id !== fieldId);
                return { success: true, fieldId };
            });
        },

        handleCreateTableFilter(payload) {
            // Filtre oluşturma işlemi
            const filters = payload.filters || [];

            // Emit edilen event, parent bileşenin filtre değişikliğini yakalaması için
            this.$emit("filters-updated", filters);

            // Success dönün
            return Promise.resolve({ success: true, filters });
        },

        handleCreateTableGroup(payload) {
            // Gruplandırma işlemi
            const { fieldId, direction, showEmptyGroups } = payload;

            // Emit edilen event, parent bileşenin grup değişikliğini yakalaması için
            this.$emit("group-updated", { fieldId, direction, showEmptyGroups });

            // Success dönün
            return Promise.resolve({ success: true, groupValue: payload });
        },

        handleCreateTableHiddenFields(payload) {
            // Alan gizleme işlemi
            const hiddenFields = payload.hiddenFields || [];

            // Emit edilen event, parent bileşenin gizli alanları güncellemesi için
            this.$emit("hidden-fields-updated", hiddenFields);

            // Success dönün
            return Promise.resolve({ success: true, hiddenFields });
        },

        handleCreateTableColorConditions(payload) {
            // Koşullu renklendirme işlemi
            const colorConditions = payload.colorConditions || [];

            // Emit edilen event, parent bileşenin renk koşullarını güncellemesi için
            this.$emit("color-conditions-updated", colorConditions);

            // Success dönün
            return Promise.resolve({ success: true, colorConditions });
        },

        handleCreateMockRecords(payload) {
            console.log("[handleCreateMockRecords] payload", payload);
            // Mock kayıt oluşturma işlemi

            // Kaç adet kayıt oluşturulduğu bilgisini alıp kullanıcıya gösterelim
            let recordCount = 0;

            if (Array.isArray(payload)) {
                recordCount = payload.length;

                // API üzerinden kayıtları oluştur - direkt payload'ı geçiyoruz
                return TABLE_API.createRecordMulti(this.tableId, payload)
                    .then(() => {
                        // Tabloyu yenilemek için bir event yayınlayalım
                        this.$emit("multi-record-created");

                        // Başarılı cevap dön
                        return {
                            success: true,
                            message: `${recordCount} adet örnek kayıt oluşturuldu`,
                        };
                    })
                    .catch((err) => {
                        console.error("Mock kayıt oluşturma hatası:", err);
                        return Promise.reject(new Error("Mock kayıtlar oluşturulamadı"));
                    });
            }

            // Geçersiz format durumunda hata dön
            return Promise.reject(new Error("Geçersiz mock kayıt formatı"));
        },

        replaceAllPlaceholdersInCommands(oldPlaceholder, newValue) {
            // Tüm komutların JSON string'ini alıp placeholder'ı değiştiriyoruz
            const commandsString = JSON.stringify(this.commands);
            // Regex ile eski placeholder geçen yerleri yeni değer ile değiştir
            const updatedString = commandsString.replace(
                new RegExp(oldPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
                newValue
            );
            // Tekrar parse edilerek this.commands güncelleniyor
            this.commands = JSON.parse(updatedString);
        },

        handleClose() {
            // Kapatıldığında işlemlerin devam etmemesi için
            this.$emit("close");
        },

        startSlideShow() {
            this.slideInterval = setInterval(() => {
                this.slideNext();
            }, 5000); // Her 5 saniyede bir kayar
        },

        stopSlideShow() {
            if (this.slideInterval) {
                clearInterval(this.slideInterval);
            }
        },

        slideNext() {
            const slider = this.$refs.slider;
            if (!slider) return;

            this.currentSlide = (this.currentSlide + 1) % (this.examplePrompts.length - 1);
            slider.scrollTo({
                left: this.currentSlide * (slider.offsetWidth / 2),
                behavior: "smooth",
            });
        },

        slidePrev() {
            const slider = this.$refs.slider;
            if (!slider) return;

            this.currentSlide = this.currentSlide === 0 ? this.examplePrompts.length - 2 : this.currentSlide - 1;
            slider.scrollTo({
                left: this.currentSlide * (slider.offsetWidth / 2),
                behavior: "smooth",
            });
        },

        selectExamplePrompt(prompt) {
            this.customerPrompt = prompt;
            // Seçilen kartı vurgulayalım ve input alanına scroll yapalım
            this.$nextTick(() => {
                const textArea = document.querySelector(".prompt-container textarea");
                if (textArea) {
                    textArea.focus();
                    textArea.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            });
        },

        runSuccessConfetti() {
            const defaults = {
                spread: 360,
                ticks: 50,
                gravity: 0,
                decay: 0.94,
                startVelocity: 30,
                colors: ["FFE400", "FFBD00", "E89400", "FFCA6C", "FDFFB8"],
            };

            const shoot = () => {
                confetti({
                    ...defaults,
                    particleCount: 40,
                    scalar: 1.2,
                    shapes: ["star"],
                });

                confetti({
                    ...defaults,
                    particleCount: 10,
                    scalar: 0.75,
                    shapes: ["circle"],
                });
            };

            shoot();
            setTimeout(shoot, 200);
            setTimeout(shoot, 300);
            setTimeout(shoot, 400);
        },

        handleKeyDown(event) {
            // Ana komponent üzerinde gerekirse kullanılabilir
        },

        handleKeyUp(event) {
            // Ana komponent üzerinde gerekirse kullanılabilir
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep .table-ai-agent-modal {
    .ant-modal {
        top: 50%;
        transform: translateY(-50%);
        padding-bottom: 0;
    }
}

.table-ai-agent {
    position: relative;
    outline: none;
}

.prompt-stage {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
    gap: 40px;

    .prompt-container {
        width: 100%;
        max-width: 600px;
        text-align: center;

        h4 {
            margin-bottom: 16px;
        }

        .assistant-capabilities {
            margin-bottom: 20px;
            background-color: #f8f9fa;
            border-radius: 8px;
            padding: 12px;
            text-align: left;
            border: 1px dashed #d9d9d9;

            .capabilities-header {
                display: flex;
                align-items: center;
                margin-bottom: 10px;

                h5 {
                    margin: 0;
                    margin-left: 8px;
                    font-weight: 600;
                    font-size: 14px;
                }

                .anticon {
                    color: #1890ff;
                    font-size: 16px;
                }
            }

            .capabilities-content {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 8px;

                .capability-item {
                    display: flex;
                    align-items: center;

                    .anticon {
                        margin-right: 8px;
                        color: #1890ff;
                    }
                    .material-icons {
                        margin-right: 8px;
                        color: #1890ff;
                        font-size: 16px;
                    }

                    span {
                        font-size: 12px;
                    }
                }
            }
        }

        .submit-button {
            margin-top: 16px;
        }
    }
}

.example-prompts {
    width: 100%;
    max-width: 800px;

    h4 {
        text-align: center;
        margin-bottom: 16px;
    }

    .prompt-slider-container {
        position: relative;
        width: 100%;
        max-width: 800px;
        margin: 0 auto;
        padding: 0 40px;

        .prompt-slider {
            display: flex;
            overflow-x: hidden;
            scroll-snap-type: x mandatory;
            gap: 24px;
            padding: 16px 0;
            scroll-behavior: smooth;

            .prompt-slide {
                flex: 0 0 calc(50% - 12px);
                scroll-snap-align: start;
            }
        }

        .slider-controls {
            position: absolute;
            width: calc(100% + 80px);
            top: 50%;
            left: -40px;
            transform: translateY(-50%);
            display: flex;
            justify-content: space-between;
            pointer-events: none;

            .control-btn {
                pointer-events: auto;
                background: white;
                border: 1px solid #e8e8e8;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.3s ease;
                border-radius: 50%;
                z-index: 2;

                .anticon {
                    font-size: 18px;
                }

                &:hover {
                    background: #1890ff;
                    border-color: #1890ff;
                    color: white;
                }
            }
        }
    }
}

.prompt-card {
    background: #f0f7ff;
    border: 1px solid #d9e8ff;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    cursor: pointer;

    .anticon {
        font-size: 32px;
        color: #1890ff;
        margin-bottom: 16px;
    }

    p {
        margin: 0;
        color: #454545;
        font-size: 14px;
        line-height: 1.6;
    }

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
        background: #e6f1ff;
        cursor: pointer;
    }

    &:active {
        transform: scale(0.98);
    }
}

.wizard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    h3 {
        margin: 0;
    }

    .close-button {
        cursor: pointer;
        font-size: 16px;

        &:hover {
            color: #f5222d;
        }
    }
}

.wizard-steps {
    max-height: calc(80vh - 200px);
    overflow-y: auto;
    padding: 16px;
    background: #f9f9f9;
    border-radius: 8px;

    .wizard-step {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        padding: 8px 12px;
        border-radius: 4px;
        background: white;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

        &.in-progress {
            color: #1890ff;
            background: #e6f7ff;
        }

        &.success {
            color: #52c41a;
            background: #f6ffed;
        }

        &.error {
            color: #f5222d;
            background: #fff1f0;
        }

        &.waiting {
            color: #8c8c8c;
            background: #fafafa;
        }
    }
}
</style>
